import { useState } from 'react'
import { api } from '@/lib/api'
import { useProjects } from '@/hooks/useProjects'
import { useCreateAsset } from '@/hooks/useMutations'
import { Loading } from '@/components/ui/Loading'
import { ErrorState } from '@/components/ui/ErrorState'
import { EmptyState } from '@/components/ui/EmptyState'
import { Modal } from '@/components/ui/Modal'
import { useQuery } from '@tanstack/react-query'
import { daysUntil } from '@/lib/format'
import type { Asset, AssetType } from '@/types/api'

const TYPE_ICONS: Record<string, string> = {
  domain: '🌐',
  ssl_certificate: '🔒',
  api_key: '🔑',
  oauth_token: '🔑',
  third_party_account: '👤',
  certificate: '📜',
  other: '📦',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 40,
  padding: '0 12px',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
  outline: 'none',
  background: 'var(--color-surface)',
  color: 'var(--color-text)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  marginBottom: 4,
  marginTop: 16,
}

export default function AssetsPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['assets'],
    queryFn: () => api.assets.list(),
    select: (res) => res.data,
  })
  const { data: projects } = useProjects()
  const createAsset = useCreateAsset()

  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState({
    type: 'domain' as AssetType,
    name: '',
    provider: '',
    expiresAt: '',
    projectId: '',
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    createAsset.mutate(
      {
        type: form.type,
        name: form.name,
        provider: form.provider || undefined,
        expiresAt: form.expiresAt || undefined,
        projectId: form.projectId || undefined,
      },
      {
        onSuccess: () => {
          setModalOpen(false)
          setForm({ type: 'domain', name: '', provider: '', expiresAt: '', projectId: '' })
        },
      },
    )
  }

  if (isLoading) return <Loading variant="rows" count={6} />

  if (error) {
    return (
      <ErrorState
        error={error}
        onRetry={() => refetch()}
        title="에셋을 불러올 수 없습니다"
      />
    )
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
        <div />
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          style={{
            padding: '8px 16px',
            background: 'var(--color-primary)',
            color: '#fff',
            borderRadius: 'var(--radius-sm)',
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          + 에셋 추가
        </button>
      </div>

      {data && data.length === 0 && (
        <EmptyState
          icon={<span>🔑</span>}
          title="에셋이 없습니다"
          description="도메인, SSL, API 키를 추가해 만료를 추적하세요."
        />
      )}
      {data && data.length > 0 && (
        <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-surface-alt)' }}>
                <th style={{ textAlign: 'left', padding: 'var(--space-md) var(--space-lg)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-tertiary)' }}>타입</th>
                <th style={{ textAlign: 'left', padding: 'var(--space-md) var(--space-lg)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-tertiary)' }}>이름</th>
                <th style={{ textAlign: 'left', padding: 'var(--space-md) var(--space-lg)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-tertiary)' }}>제공자</th>
                <th style={{ textAlign: 'right', padding: 'var(--space-md) var(--space-lg)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-tertiary)' }}>만료</th>
              </tr>
            </thead>
            <tbody>
              {data.map((asset: Asset) => {
                const days = daysUntil(asset.expiresAt)
                const expiryColor =
                  days === null
                    ? 'var(--color-text-tertiary)'
                    : days <= 7
                      ? 'var(--color-danger-text)'
                      : days <= 30
                        ? 'var(--color-cost-text)'
                        : 'var(--color-text-secondary)'
                const expiryLabel =
                  days === null
                    ? '—'
                    : days < 0
                      ? `${Math.abs(days)}일 전 만료`
                      : `${days}일 후`
                return (
                  <tr key={asset.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: 'var(--space-md) var(--space-lg)', fontSize: 16 }}>
                      {TYPE_ICONS[asset.type] ?? '📦'}
                    </td>
                    <td style={{ padding: 'var(--space-md) var(--space-lg)', fontWeight: 600 }}>
                      {asset.name}
                    </td>
                    <td style={{ padding: 'var(--space-md) var(--space-lg)', color: 'var(--color-text-secondary)' }}>
                      {asset.provider ?? '—'}
                    </td>
                    <td style={{ padding: 'var(--space-md) var(--space-lg)', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600, color: expiryColor }}>
                      {expiryLabel}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="에셋 추가">
        <form onSubmit={handleSubmit}>
          <label style={labelStyle} htmlFor="a-type">타입</label>
          <select
            id="a-type"
            style={inputStyle}
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as AssetType })}
          >
            <option value="domain">Domain (도메인)</option>
            <option value="ssl_certificate">SSL Certificate (인증서)</option>
            <option value="api_key">API Key</option>
            <option value="oauth_token">OAuth Token</option>
            <option value="third_party_account">Third-party Account</option>
            <option value="certificate">Certificate</option>
            <option value="other">Other</option>
          </select>

          <label style={labelStyle} htmlFor="a-name">이름</label>
          <input
            id="a-name"
            style={inputStyle}
            type="text"
            placeholder="예: example.com, Stripe API Key"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />

          <label style={labelStyle} htmlFor="a-provider">제공자</label>
          <input
            id="a-provider"
            style={inputStyle}
            type="text"
            placeholder="예: Cloudflare, Namecheap, AWS"
            value={form.provider}
            onChange={(e) => setForm({ ...form, provider: e.target.value })}
          />

          <label style={labelStyle} htmlFor="a-expires">만료일 (선택)</label>
          <input
            id="a-expires"
            style={inputStyle}
            type="date"
            value={form.expiresAt}
            onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
          />

          <label style={labelStyle} htmlFor="a-project">프로젝트 (선택)</label>
          <select
            id="a-project"
            style={inputStyle}
            value={form.projectId}
            onChange={(e) => setForm({ ...form, projectId: e.target.value })}
          >
            <option value="">선택 안함</option>
            {projects?.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <button
            type="submit"
            disabled={createAsset.isPending}
            style={{
              marginTop: 24,
              width: '100%',
              height: 44,
              background: 'var(--color-primary)',
              color: '#fff',
              borderRadius: 'var(--radius-sm)',
              fontWeight: 600,
              fontSize: 14,
              opacity: createAsset.isPending ? 0.6 : 1,
            }}
          >
            {createAsset.isPending ? '추가 중...' : '에셋 추가'}
          </button>
          {createAsset.error && (
            <p style={{ marginTop: 8, fontSize: 13, color: 'var(--color-danger-text)' }}>
              {(createAsset.error as Error).message}
            </p>
          )}
        </form>
      </Modal>
    </>
  )
}
